import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ArrowRight, Download, FileText } from 'lucide-react';
import { HorizontalBorderStack } from '../editorial/EditorialLayouts';

const EASE = [0.22, 1, 0.36, 1];

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCTS = [
  {
    short: 'Ortho', certification: 'Orthopedic Applications',
    image: '/flexioss-ortho-transparent.png',
    imageAlt: 'FlexiOss Ortho biphasic bone substitute for orthopedic trauma surgery and long bone defect reconstruction',
    description: 'Third-generation biomaterial with mechanical parameters similar to cancellous bone. Suitable for defects up to 7 cm in length.',
    applications: ['Comminuted fractures with bone-tissue defects', 'Long-bone defect filling and reconstruction', 'Trauma surgery and emergency medicine', 'Bone-pocket filling for tissue regeneration'],
    accent: '#3b82f6',
    infoLink: '/flexioss-ortho',
    ifuLink: `${API}/api/document/ortho-ifu`,
    brochureLink: `${API}/api/document/ortho-catalogue`,
  },
  {
    short: 'Dent', certification: 'Dental & Maxillofacial',
    image: '/flexioss-dent-transparent.png',
    imageAlt: 'FlexiOss Dent biphasic biomaterial for dental maxillofacial procedures and alveolar bone augmentation',
    description: 'Implantable biomaterial for dental and maxillofacial procedures. Flexible, bioactive, available in 1 cm, 3 cm and 5 cm sizes.',
    applications: ['Post-extraction alveolar augmentation', 'Dental implant site preparation', 'Maxillofacial reconstructive procedures', 'Defect filling after cystotomy and root resection'],
    accent: '#E91E8C',
    infoLink: '/flexioss-dent',
    ifuLink: `${API}/api/document/dent-ifu`,
    brochureLink: `${API}/api/document/dent-catalogue`,
  },
  {
    short: 'Vet', certification: 'Veterinary Applications',
    image: '/flexioss-vet.png',
    imageAlt: 'FlexiOss Vet veterinary bone regenerative biomaterial for companion animal orthopedic and dental procedures',
    description: '3rd-generation veterinary biomaterial. Absorbs blood and drug solutions, acts as antibiotic carrier. Bone healing time halved.',
    applications: ['Teeth extraction and bone filling', 'Oronasal fistulae treatment', 'Angular deformity correction (osteotomy)', 'Periodontal disease and EOTRH syndrome'],
    accent: '#4CAF50',
    infoLink: '/flexioss-vet',
    ifuLink: `${API}/api/document/vet-ifu`,
    brochureLink: `${API}/api/document/vet-catalogue`,
  },
];

const triggerDownload = (url) => {
  const link = document.createElement('a');
  link.href = url; link.target = '_blank'; link.rel = 'noopener noreferrer';
  document.body.appendChild(link); link.click(); document.body.removeChild(link);
};

const ExpandedContent = ({ p, idx }) => (
  <div className="grid h-full grid-rows-[auto_1fr_auto] gap-6">
    <div>
      <h3 className="font-display text-3xl font-black tracking-tight text-white xl:text-4xl">
        FlexiOss<sup className="text-base">®</sup> <span style={{ color: p.accent }}>{p.short}</span>
      </h3>
      <p className="mt-2 font-display text-[11px] uppercase tracking-[0.22em]" style={{ color: p.accent }}>
        PH-FDA · {p.certification}
      </p>
    </div>
    <div className="grid items-center gap-6 lg:grid-cols-[1.1fr_1fr]">
      <div>
        <p className="text-sm leading-relaxed text-slate-300">{p.description}</p>
        <p className="mt-5 font-display text-[10px] uppercase tracking-[0.22em]" style={{ color: p.accent }}>Key Applications</p>
        <div className="mt-2">
          <HorizontalBorderStack items={p.applications} accent={p.accent} dense numbered={false} testid={`product-apps-${idx}`} />
        </div>
      </div>
      <div className="hidden h-40 items-center justify-center lg:flex xl:h-52">
        <img src={p.image} alt={p.imageAlt} className="h-full w-full object-contain drop-shadow-2xl" />
      </div>
    </div>
    <div className="flex flex-wrap items-center gap-3">
      <Link
        to={p.infoLink}
        data-testid={`product-more-${idx}`}
        className="group inline-flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-[0.1em] text-white transition-all duration-500"
        style={{ backgroundColor: p.accent }}
      >
        More Information
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.6} />
      </Link>
      <button onClick={() => triggerDownload(p.ifuLink, `FlexiOss_${p.short}_IFU.pdf`)} className="inline-flex items-center gap-2 border px-4 py-3 text-xs font-bold uppercase tracking-[0.1em] transition-colors duration-300 hover:bg-white/5" style={{ borderColor: p.accent, color: p.accent }}>
        <Download className="h-3.5 w-3.5" strokeWidth={1.6} /> IFU
      </button>
      <button onClick={() => triggerDownload(p.brochureLink, `FlexiOss_${p.short}_Brochure.pdf`)} className="inline-flex items-center gap-2 border px-4 py-3 text-xs font-bold uppercase tracking-[0.1em] transition-colors duration-300 hover:bg-white/5" style={{ borderColor: p.accent, color: p.accent }}>
        <FileText className="h-3.5 w-3.5" strokeWidth={1.6} /> Brochure
      </button>
    </div>
  </div>
);

const ProductLineSection = () => {
  const [active, setActive] = useState(0);

  return (
    <section id="products" data-testid="product-line-section" className="bg-[#000a14] px-6 py-24 sm:px-10 lg:px-16">
      <div className="mx-auto max-w-[1600px]">
        <div className="mb-12 max-w-3xl">
          <p className="font-display text-[11px] font-bold uppercase tracking-[0.4em] text-blue-400">The Product Family</p>
          <h2 className="mt-5 font-display text-4xl font-black leading-[1.02] tracking-tight text-white sm:text-5xl lg:text-6xl">
            One composition. <span className="text-white/40">Three specialisations.</span>
          </h2>
        </div>

        {/* Desktop — horizontal expanding accordion */}
        <div className="hidden h-[64vh] w-full border border-white/10 lg:flex" data-testid="product-accordion-desktop">
          {PRODUCTS.map((p, i) => {
            const isActive = active === i;
            return (
              <motion.div
                key={i}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                tabIndex={0}
                data-testid={`product-pillar-${i}`}
                className="relative cursor-pointer overflow-hidden border-l border-white/10 outline-none first:border-l-0"
                animate={{ flexGrow: isActive ? 7 : 1, flexBasis: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                style={{ flexGrow: isActive ? 7 : 1, flexBasis: 0, minWidth: 0 }}
              >
                <div className="absolute left-0 right-0 top-0 z-20 h-1 transition-opacity duration-500" style={{ backgroundColor: p.accent, opacity: isActive ? 1 : 0.4 }} />
                <span aria-hidden="true" className="pointer-events-none absolute -bottom-6 right-2 z-0 font-display text-[10rem] font-black leading-none text-white/[0.04]">{String(i + 1)}</span>

                {/* Collapsed vertical label */}
                <AnimatePresence>
                  {!isActive && (
                    <motion.div
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <span className="font-display text-2xl font-black uppercase tracking-[0.15em] text-white/80 [writing-mode:vertical-rl] rotate-180">
                        FlexiOss {p.short}
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Expanded content */}
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                      transition={{ duration: 0.4, delay: 0.15 }}
                      className="relative z-10 h-full p-10 xl:p-12"
                    >
                      <ExpandedContent p={p} idx={i} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Mobile — vertical accordion */}
        <div className="border border-white/10 lg:hidden" data-testid="product-accordion-mobile">
          {PRODUCTS.map((p, i) => {
            const isActive = active === i;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.55, ease: EASE, delay: i * 0.08 }}
                className="border-b border-white/10 last:border-b-0"
              >
                <button
                  onClick={() => setActive(isActive ? -1 : i)}
                  data-testid={`product-acc-${i}`}
                  aria-expanded={isActive}
                  className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left"
                >
                  <span className="flex items-center gap-3">
                    <span className="h-8 w-1" style={{ backgroundColor: p.accent }} />
                    <span className="font-display text-xl font-black uppercase tracking-wide text-white">
                      FlexiOss {p.short}
                    </span>
                  </span>
                  <ChevronDown className={`h-5 w-5 flex-shrink-0 transition-transform duration-300 ${isActive ? 'rotate-180' : ''}`} style={{ color: isActive ? p.accent : '#64748b' }} strokeWidth={1.6} />
                </button>
                <AnimatePresence>
                  {isActive && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: EASE }}
                      className="overflow-hidden"
                    >
                      <div className="px-5 pb-7 pt-1">
                        <ExpandedContent p={p} idx={i} />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* Shared composition — hairline editorial strip */}
        <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
          {[
            { v: '~65%', k: 'HAP Granule Porosity' },
            { v: 'β-1,3', k: 'Glucan Polymer Binder' },
            { v: '100%', k: 'Synthetic · Animal-Free' },
          ].map((c, i) => (
            <motion.div
              key={c.k}
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, ease: EASE, delay: i * 0.1 }}
              className="bg-[#000a14] px-8 py-8"
            >
              <p className="font-display text-4xl font-black tracking-tight text-white">{c.v}</p>
              <p className="mt-1 text-sm text-slate-400">{c.k}</p>
            </motion.div>
          ))}
        </div>
        <p className="mt-5 text-sm text-slate-500">
          Every FlexiOss® product shares one proven biphasic composition. Exclusively distributed in the Philippines by{' '}
          <span className="font-semibold text-[#E87A20]">Fortschritt Medical Supplies Inc.</span>
        </p>
      </div>
    </section>
  );
};

export default ProductLineSection;
